import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  Tag,
  Typography,
  Upload,
  Image,
  Popconfirm,
  Radio,
  Tooltip,
} from "antd";
import type { UploadProps } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UploadOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { galleryAPI } from "../../utils/api";

const { Title } = Typography;
const { TextArea } = Input;

const API_URL = process.env.REACT_APP_API_URL;

interface GalleryItem {
  _id: string;
  type: "image" | "video";
  url: string;
  thumbnail?: string;
  title: string;
  description: string;
  location: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

interface GalleryFormData {
  type: "image" | "video";
  url: string;
  thumbnail?: string;
  title: string;
  description: string;
  location: string;
}

interface GalleryManagementProps {
  active: string;
}

const GalleryManagement: React.FC<GalleryManagementProps> = ({ active }) => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [form] = Form.useForm<GalleryFormData>();
  const hasFetchedRef = useRef<boolean>(false);

  const [itemType, setItemType] = useState<"image" | "video">("image");

  useEffect(() => {
    if (active === "gallery" && !hasFetchedRef.current) {
      fetchGalleryItems();
      hasFetchedRef.current = true;
    }
  }, [active]);

  const fetchGalleryItems = async () => {
    try {
      setLoading(true);
      const response = await galleryAPI.getAllAdmin();
      setItems(response.data.items);
    } catch (error) {
      console.error("Error fetching gallery items:", error);
      message.error("Failed to load gallery items");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setImageUrl("");
    setThumbnailUrl("");
    setItemType("image");
    form.resetFields();
    form.setFieldsValue({ type: "image" });
    setModalVisible(true);
  };

  const handleEdit = (item: GalleryItem) => {
    setEditingItem(item);
    setImageUrl(item.url || "");
    setThumbnailUrl(item.thumbnail || "");
    setItemType(item.type);
    form.setFieldsValue({
      type: item.type,
      title: item.title,
      description: item.description,
      location: item.location,
      url: item.url,
      thumbnail: item.thumbnail || "",
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await galleryAPI.delete(id);
      message.success("Gallery item deleted successfully");
      fetchGalleryItems();
    } catch (error) {
      console.error("Error deleting gallery item:", error);
      message.error("Failed to delete gallery item");
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await galleryAPI.toggle(id);
      message.success("Gallery item visibility updated");
      fetchGalleryItems();
    } catch (error) {
      console.error("Error toggling gallery item:", error);
      message.error("Failed to update gallery item");
    }
  };

  // Image upload handler
  const handleImageUpload: UploadProps["customRequest"] = async (options) => {
    const { file, onSuccess, onError } = options;

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("image", file);

      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/upload/image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}. ${errorText}`,
        );
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const responseText = await response.text();
        console.error("Non-JSON response received:", responseText);
        throw new Error("Server returned non-JSON response");
      }

      const data = await response.json();

      if (data.success) {
        let uploadedImageUrl = data.imageUrl;

        if (!uploadedImageUrl.startsWith("http")) {
          uploadedImageUrl = `${API_URL}${uploadedImageUrl}`;
        }

        setImageUrl(uploadedImageUrl);
        form.setFieldsValue({ url: uploadedImageUrl });
        message.success("Image uploaded successfully");
        onSuccess?.(data, file as any);
      } else {
        throw new Error(data.message || "Upload failed");
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      message.error(error.message || "Failed to upload image");
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  // Thumbnail upload handler (for videos)
  const handleThumbnailUpload: UploadProps["customRequest"] = async (
    options,
  ) => {
    const { file, onSuccess, onError } = options;

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("image", file);

      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/upload/image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        let uploadedImageUrl = data.imageUrl;

        if (!uploadedImageUrl.startsWith("http")) {
          uploadedImageUrl = `${API_URL}${uploadedImageUrl}`;
        }

        setThumbnailUrl(uploadedImageUrl);
        form.setFieldsValue({ thumbnail: uploadedImageUrl });
        message.success("Thumbnail uploaded successfully");
        onSuccess?.(data, file as any);
      } else {
        throw new Error(data.message || "Upload failed");
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      message.error(error.message || "Failed to upload thumbnail");
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handleImageRemove = () => {
    setImageUrl("");
    form.setFieldsValue({ url: "" });
  };

  const handleThumbnailRemove = () => {
    setThumbnailUrl("");
    form.setFieldsValue({ thumbnail: "" });
  };

  const handleSubmit = async (values: GalleryFormData) => {
    try {
      const itemData = {
        type: values.type,
        url: imageUrl || values.url,
        thumbnail:
          values.type === "video" ? thumbnailUrl || values.thumbnail : "",
        title: values.title,
        description: values.description,
        location: values.location,
      };

      if (editingItem) {
        await galleryAPI.update(editingItem._id, itemData);
        message.success("Gallery item updated successfully");
      } else {
        await galleryAPI.create(itemData);
        message.success("Gallery item created successfully");
      }

      setModalVisible(false);
      setImageUrl("");
      setThumbnailUrl("");
      form.resetFields();
      fetchGalleryItems();
    } catch (error) {
      console.error("Error saving gallery item:", error);
      message.error("Failed to save gallery item");
    }
  };

  const columns = [
    {
      title: "Preview",
      dataIndex: "url",
      key: "preview",
      width: 120,
      render: (url: string, record: GalleryItem) =>
        record.type === "image" ? (
          <Image
            src={url}
            alt={record.title}
            width={70}
            height={50}
            style={{ objectFit: "cover", borderRadius: "4px" }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
        ) : (
          <div style={{ position: "relative", width: 70, height: 50 }}>
            <Image
              src={record.thumbnail || url}
              alt={record.title}
              width={70}
              height={50}
              style={{ objectFit: "cover", borderRadius: "4px" }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
            />
            <PlayCircleOutlined
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: 32,
                color: "white",
                textShadow: "0 2px 4px rgba(0,0,0,0.5)",
              }}
            />
          </div>
        ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 50,
      render: (type: string) => (
        <Tag
          color={type === "image" ? "blue" : "purple"}
          icon={type === "image" ? <PictureOutlined /> : <PlayCircleOutlined />}
        />
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string) => (
        <Tooltip title={text}>
          {text.length > 20 ? `${text.slice(0, 20)}...` : text}
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "VISIBLE" : "HIDDEN"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_: any, record: GalleryItem) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={record.isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => handleToggle(record._id)}
          >
            {record.isActive ? "Hide" : "Show"}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this item?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Gallery Management
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Gallery Item
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingItem ? "Edit Gallery Item" : "Add New Gallery Item"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setImageUrl("");
          setThumbnailUrl("");
          form.resetFields();
        }}
        footer={null}
        width="95%"
        style={{ maxWidth: 800 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ type: "image" }}
        >
          {/* Type Selection */}
          <Form.Item
            name="type"
            label="Media Type"
            rules={[{ required: true, message: "Please select media type" }]}
          >
            <Radio.Group onChange={(e) => setItemType(e.target.value)}>
              <Radio.Button value="image">
                <PictureOutlined /> Image
              </Radio.Button>
              <Radio.Button value="video">
                <PlayCircleOutlined /> Video
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* Image/Video Upload */}
          <Form.Item
            label={
              itemType === "image"
                ? "Upload Image"
                : "Upload Video or Enter URL"
            }
          >
            <div style={{ marginBottom: 16 }}>
              {imageUrl ? (
                <div style={{ position: "relative", display: "inline-block" }}>
                  {itemType === "image" ? (
                    <Image
                      src={imageUrl}
                      alt="Preview"
                      width={200}
                      height={150}
                      style={{ objectFit: "cover", borderRadius: "8px" }}
                    />
                  ) : (
                    <div style={{ position: "relative" }}>
                      <video
                        src={imageUrl}
                        width={200}
                        height={150}
                        style={{ borderRadius: "8px" }}
                        controls
                      />
                    </div>
                  )}
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={handleImageRemove}
                    style={{ position: "absolute", top: 8, right: 8 }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <Upload
                  name="media"
                  listType="picture-card"
                  showUploadList={false}
                  customRequest={handleImageUpload}
                  accept={itemType === "image" ? "image/*" : "video/*"}
                  disabled={uploading}
                >
                  <div>
                    {uploading ? <LoadingOutlined /> : <UploadOutlined />}
                    <div style={{ marginTop: 8 }}>
                      {uploading
                        ? "Uploading..."
                        : `Upload ${itemType === "image" ? "Image" : "Video"}`}
                    </div>
                  </div>
                </Upload>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="url"
            label={
              itemType === "image" ? "Or Enter Image URL" : "Or Enter Video URL"
            }
            help={
              itemType === "video"
                ? "For YouTube videos, use the embed URL"
                : undefined
            }
          >
            <Input
              placeholder={
                itemType === "image"
                  ? "https://example.com/image.jpg"
                  : "https://www.youtube.com/embed/VIDEO_ID"
              }
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </Form.Item>

          {/* Video Thumbnail Upload */}
          {itemType === "video" && (
            <>
              <Form.Item label="Video Thumbnail">
                <div style={{ marginBottom: 16 }}>
                  {thumbnailUrl ? (
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <Image
                        src={thumbnailUrl}
                        alt="Thumbnail"
                        width={200}
                        height={150}
                        style={{ objectFit: "cover", borderRadius: "8px" }}
                      />
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={handleThumbnailRemove}
                        style={{ position: "absolute", top: 8, right: 8 }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Upload
                      name="thumbnail"
                      listType="picture-card"
                      showUploadList={false}
                      customRequest={handleThumbnailUpload}
                      accept="image/*"
                      disabled={uploading}
                    >
                      <div>
                        {uploading ? <LoadingOutlined /> : <UploadOutlined />}
                        <div style={{ marginTop: 8 }}>
                          {uploading ? "Uploading..." : "Upload Thumbnail"}
                        </div>
                      </div>
                    </Upload>
                  )}
                </div>
              </Form.Item>

              <Form.Item name="thumbnail" label="Or Enter Thumbnail URL">
                <Input
                  placeholder="https://example.com/thumbnail.jpg"
                  onChange={(e) => setThumbnailUrl(e.target.value)}
                />
              </Form.Item>
            </>
          )}

          {/* Title */}
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter title" }]}
          >
            <Input placeholder="e.g., Sigiriya Rock Fortress" />
          </Form.Item>

          {/* Description */}
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <TextArea
              rows={3}
              placeholder="Describe the location or moment..."
            />
          </Form.Item>

          {/* Location */}
          <Form.Item
            name="location"
            label="Location"
            rules={[{ required: true, message: "Please enter location" }]}
          >
            <Input placeholder="e.g., Sigiriya, Kandy, Ella" />
          </Form.Item>

          {/* Submit Button */}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={uploading}>
                {editingItem ? "Update" : "Create"} Gallery Item
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setImageUrl("");
                  setThumbnailUrl("");
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GalleryManagement;
